import React from "react";
import {Components} from "@webtech/webtech-ui-react/build/webtech-ui";
import {GameRTPProfilePayload} from "../../../configurations/components/Utils/rtp-profiles";

import "./RemoteUpdateOptions.component.scss";

interface RemoteUpdateOptionsProps {
    includeServerConfig: boolean;
    includeGameLogos: boolean;
    showServerConfig: boolean;
    showGameLogos: boolean;
    gameRTPProfiles: GameRTPProfilePayload[];
    onServerConfigChange: (value: boolean) => void;
    onGameLogosChange: (value: boolean) => void;
    onRTPProfileForceChange: (index: number, value: boolean) => void;
    onAllRTPProfilesForceChange: (value: boolean) => void;
}

export const supportsRemoteRTPProfileUpdate = (version?: string | null) => {
    if (!version) return false;

    const versionParts = version.split(".").map(part => parseInt(part, 10));
    const major = versionParts[0] || 0;
    const minor = versionParts[1] || 0;

    return major > 2 || (major === 2 && minor >= 10);
};

export const supportsRemoteGameLogoUpdate = (version?: string | null) => {
    if (!version) return false;

    const versionParts = version.split(".").map(part => parseInt(part, 10));
    const major = versionParts[0] || 0;
    const minor = versionParts[1] || 0;

    return major > 2 || (major === 2 && minor >= 11);
};

export const getRemoteUpdateIncludes = (
    version: string | null | undefined,
    includeServerConfig: boolean,
    includeGameLogos: boolean,
    gameRTPProfiles: GameRTPProfilePayload[] = []
) => ({
    include_server_config: supportsRemoteGameLogoUpdate(version)
        ? includeServerConfig
        : true,
    include_game_logos: supportsRemoteGameLogoUpdate(version)
        ? includeGameLogos
        : false,
    game_rtp_profiles: supportsRemoteRTPProfileUpdate(version)
        ? prepareRemoteRTPProfiles(gameRTPProfiles).map(profile => {
              const {game_name, ...configurationProfile} = profile;

              return {
                  ...configurationProfile,
                  force: Boolean(profile.force)
              };
          })
        : []
});

export const prepareRemoteRTPProfiles = (
    gameRTPProfiles: GameRTPProfilePayload[] = []
) =>
    Array.isArray(gameRTPProfiles)
        ? gameRTPProfiles.map(profile => ({
              ...profile,
              force: Boolean(profile.force)
          }))
        : [];

export const formatDefaultRTPProfile = (
    profile: string | number,
    defaultProfile?: string | number | null
) => {
    if (defaultProfile === undefined || defaultProfile === null) return "—";

    const profileValue = `${profile}`;
    const defaultValue = `${defaultProfile}`;
    const versionCharacter = /^[A-Za-z]/.test(profileValue)
        ? profileValue.charAt(0)
        : "";
    const defaultWithoutVersionCharacter = /^[A-Za-z]/.test(defaultValue)
        ? defaultValue.substring(1)
        : defaultValue;

    return `${versionCharacter}${defaultWithoutVersionCharacter}`;
};

const RemoteUpdateOptions = ({
    includeServerConfig,
    includeGameLogos,
    showServerConfig,
    showGameLogos,
    gameRTPProfiles,
    onServerConfigChange,
    onGameLogosChange,
    onRTPProfileForceChange,
    onAllRTPProfilesForceChange
}: RemoteUpdateOptionsProps) => {
    const allRTPProfilesForced =
        gameRTPProfiles.length > 0 &&
        gameRTPProfiles.every(profile => Boolean(profile.force));

    return (
        <div className="remote-update-options">
        {showServerConfig && (
            <Components.Checkbox
                checked={includeServerConfig}
                triggerChange={false}
                onChange={onServerConfigChange}
            >
                Server Configuration
            </Components.Checkbox>
        )}
        {showGameLogos && (
            <Components.Checkbox
                checked={includeGameLogos}
                triggerChange={false}
                onChange={onGameLogosChange}
            >
                Game Logos
            </Components.Checkbox>
        )}
        {gameRTPProfiles.length > 0 && (
            <div className="rtp-profile-options">
                <span>RTP Profiles to Force</span>
                <div className="rtp-profile-table-scroll">
                    <table className="rtp-profile-table">
                        <thead>
                            <tr>
                                <th>Game</th>
                                <th>Default RTP Profile</th>
                                <th>RTP Profile</th>
                                <th>
                                    <Components.Switch
                                        on={allRTPProfilesForced}
                                        enabled
                                        onClick={() =>
                                            onAllRTPProfilesForceChange(
                                                !allRTPProfilesForced
                                            )
                                        }
                                    />
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {gameRTPProfiles.map((rtpProfile, index) => (
                                <tr key={`${rtpProfile.game_id}-${index}`}>
                                    <td>
                                        {rtpProfile.game_name ||
                                            `Game ${rtpProfile.game_id}`}
                                    </td>
                                    <td className="rtp-profile-value">
                                        {formatDefaultRTPProfile(
                                            rtpProfile.profile,
                                            rtpProfile.default_rtp_profile
                                        )}
                                    </td>
                                    <td className="rtp-profile-value">
                                        {rtpProfile.profile}
                                    </td>
                                    <td className="rtp-profile-force">
                                        <Components.Switch
                                            on={Boolean(rtpProfile.force)}
                                            enabled
                                            onClick={() =>
                                                onRTPProfileForceChange(
                                                    index,
                                                    !Boolean(rtpProfile.force)
                                                )
                                            }
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        )}
        </div>
    );
};

export default RemoteUpdateOptions;
